import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { connectCalendar, getCalendarStatus, getCalendarBookings } from '../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCog, faCalendarAlt, faArrowLeft, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import CalendarView from '../components/calendar/CalendarView';
import { CalendarStatusResponse, BookedTimeSlot, BookedSlotsData } from '../components/calendar/types';
import BookingForm from '../components/calendar/BookingForm';
import BookingConfirmation from '../components/calendar/BookingConfirmation';
import BookingError from '../components/calendar/BookingError';
import { format } from 'date-fns';
import Loader from '../components/common/Loader';

interface BookingInfo {
  slot: {
    start: string;
    end: string;
    eventTypeId: string;
    scheduleName: string;
  };
  timeZone: string;
}

const CalendarSettings: React.FC = () => {
    const [calUsername, setCalUsername] = useState('');
    const [calApiKey, setCalApiKey] = useState('');
    const [isConnected, setIsConnected] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [calendarStatus, setCalendarStatus] = useState<CalendarStatusResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [calendarLoading, setCalendarLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const clientId = localStorage.getItem('client_id');
    
    // Booking state
    const [showBookingForm, setShowBookingForm] = useState(false);
    const [showBookingConfirmation, setShowBookingConfirmation] = useState(false);
    const [showBookingError, setShowBookingError] = useState(false);
    const [bookingInfo, setBookingInfo] = useState<BookingInfo | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<any>(null);
    const [selectedTime, setSelectedTime] = useState<string | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [bookingError, setBookingError] = useState<string | null>(null);
    const [bookings, setBookings] = useState<BookedSlotsData | null>(null);

    // Add a ref to track if initial load has happened
    const hasLoadedRef = useRef<boolean>(false);
    
    // Add a throttle timestamp to prevent rapid API calls
    const lastLoadTimeRef = useRef<number>(0);

    // Find where other state variables are declared and add:
    const [displayInUTC, setDisplayInUTC] = useState<boolean>(true); // Always use UTC
    const [displayTime, setDisplayTime] = useState<string | null>(null);

    // Effect to handle initial loading state
    useEffect(() => {
        // If there's no calendar status yet, set loading state to true
        if (!calendarStatus) {
            setCalendarLoading(true);
        }
    }, [calendarStatus]);

    useEffect(() => {
        // Set initial loading state
        setLoading(true);
        setCalendarLoading(true);
        setIsConnected(null); // Reset to "unknown" state
        
        // Only load if not already loaded or forced
        if (!hasLoadedRef.current) {
            loadCalendarData();
        }
        
        // Cleanup function to reset the ref if component unmounts
        return () => {
            hasLoadedRef.current = false;
        };
    }, []);

    const loadCalendarData = async (forceReload = false) => {
        try {
            // Check if we should skip loading based on throttle
            const now = Date.now();
            if (!forceReload && now - lastLoadTimeRef.current < 5000) {
                console.log('Skipping load due to throttle');
                return;
            }
            lastLoadTimeRef.current = now;
            
            // Start loading
            setCalendarLoading(true);
            setError(null);
            
            // Get calendar status
            console.log('Getting calendar status for client:', clientId);
            const status = await getCalendarStatus(clientId!);
            console.log('Calendar status:', status);

            // Fetch booked slots if calendar is connected
            if (status.connected && clientId) {
                try {
                    const bookedSlotsData = await getCalendarBookings(clientId);
                    console.log('Fetched booked slots raw data:', bookedSlotsData);
                    
                    // The API is returning {booked_times_by_day: {...}} directly
                    if (bookedSlotsData && bookedSlotsData.booked_times_by_day) {
                        console.log('Booked slots data structure is valid (direct format)');
                        console.log('Dates with bookings:', Object.keys(bookedSlotsData.booked_times_by_day).length);
                        
                        // Transform to match the expected BookedSlotsData type
                        // Preserving duration information for accurate time slot filtering
                        setBookings({
                            status: 'success',
                            data: {
                                booked_times_by_day: bookedSlotsData.booked_times_by_day,
                                timezone: status.timezone || 'UTC'
                            }
                        });
                    } 
                    // If it follows the expected {status, data} format
                    else if (bookedSlotsData && bookedSlotsData.status === 'success' && bookedSlotsData.data) {
                        console.log('Booked slots data structure is valid (standard format)');
                        console.log('Dates with bookings:', Object.keys(bookedSlotsData.data.booked_times_by_day).length);
                        
                        // Store the complete response with the correct structure
                        setBookings(bookedSlotsData);
                    } else {
                        console.warn('Invalid booked slots data structure:', bookedSlotsData);
                        setBookings(null);
                    }
                } catch (error) {
                    console.error('Error fetching booked slots:', error);
                    setBookings(null);
                }
            }
            
            // Update state with status
            setCalendarStatus(status);
            setIsConnected(status.connected);
            hasLoadedRef.current = true; // Mark as loaded
            setLoading(false); // Clear main loading state
            setCalendarLoading(false);
        } catch (error) {
            console.error('Error loading calendar data:', error);
            setError('Failed to load calendar data');
            setLoading(false); // Clear main loading state on error
            setCalendarLoading(false);
        }
    };

    const handleConnect = async () => {
        setIsLoading(true);
        setLoading(true); // Set the main loading state
        setCalendarLoading(true); // Set calendar loading state
        
        try {
            // Validate API key format
            if (!calApiKey.startsWith('cal_live_')) {
                toast.error('Please enter a valid live API key (starts with cal_live_)');
                setLoading(false);
                setIsLoading(false);
                return;
            }

            const response = await connectCalendar({
                username: calUsername,
                apiKey: calApiKey,
                clientId: clientId!
            });

            if (response.success) {
                toast.success('Calendar connected successfully!');
                
                // Reset the connection state before reloading data to prevent flash of wrong UI
                setIsConnected(null);
                
                // Force reload of calendar data
                await loadCalendarData(true);
                
                // Hide settings form
                setShowSettings(false);
            } else {
                // Handle unsuccessful response
                toast.error(response.message || 'Failed to connect calendar');
                setLoading(false);
                setCalendarLoading(false);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.detail || 
                error.message || 
                'Failed to connect calendar';
            toast.error(errorMessage);
            console.error('Calendar connection error:', error);
            setLoading(false);
            setCalendarLoading(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTimeSelect = (event: any, date: Date, time: string | null, timeInfo?: any) => {
        // If there's a booking confirmation showing, ignore time selections
        if (showBookingConfirmation) {
            console.log('Ignoring time selection because booking confirmation is displayed');
            return;
        }
        
        // If time is null, this is just a date selection, not a time slot selection
        if (time === null) {
            console.log('[CalendarSettings] Date selected without time slot:', date);
            return; // Don't show booking form or update state for date-only selections
        }
        
        console.log('[CalendarSettings] Time selected with timeInfo:', timeInfo);
        
        // CRITICAL FIX: Use the timeInfo.isoTime directly without any modifications
        // timeInfo now contains both the original time slot and properly formatted ISO time
        if (!timeInfo || !timeInfo.isoTime) {
            console.error('[CalendarSettings] Missing required timeInfo.isoTime - cannot proceed with booking');
            return;
        }
        
        // Store the exactly-as-provided ISO time string for booking
        // This ensures we send back the exact same time format that Cal.com provided
        const validTime = timeInfo.isoTime;
        
        console.log('[CalendarSettings] Using exact ISO time for booking:', validTime);
        console.log('[CalendarSettings] Original time slot from Cal.com:', timeInfo.originalTimeSlot);
        console.log('[CalendarSettings] Event timezone:', timeInfo.eventTimezone);
        
        // Store selected event and time for booking form
        setSelectedEvent(event);
        setSelectedDate(date);
        setSelectedTime(validTime); // Use the validated ISO time string
        
        // Record the original time slot for display purposes
        setDisplayTime(timeInfo.originalTimeSlot || time);
        
        // Show booking form - make sure no other modals are showing
        setShowBookingConfirmation(false);
        setShowBookingError(false);
        setShowBookingForm(true);
        
        console.log('[CalendarSettings] State updated, booking form should appear now');
    };
    
    const handleBookingSuccess = async (response: any) => {
        console.log('Booking response received in CalendarSettings:', response);
        
        // Skip processing if this is a "force refresh" response (second callback)
        // This is the key to preventing duplicate confirmations
        if (response && response.forceRefresh === true) {
            console.log('Skipping duplicate confirmation for force refresh response');
            return;
        }
        
        // CASE 1: Error response handling
        if (!response || response.success === false) {
            // Check if it's a time conflict error
            if (response?.error && (
                response.error.includes('time slot is no longer available') ||
                response.error.includes('User either already has booking at this time')
            )) {
                // For time conflict errors, show the booking error view
                console.log('Time conflict error detected, showing booking error view');
                
                // Format error message to be more user-friendly
                const userFriendlyError = 'This time slot is no longer available. Someone else may have booked it. Please select another time slot.';
                
                // Set the error message to be displayed
                setBookingError(userFriendlyError);
                
                // Show the booking error view
                setShowBookingForm(false);
                setShowBookingConfirmation(false);
                setShowBookingError(true);
                
                return;
            }
            
            // Check for validation errors
            if (response?.error && (
                response.error.includes('required') || 
                response.error.includes('invalid email') ||
                response.error.includes('validation')
            )) {
                // For validation errors, keep the form open with error message
                console.log('Validation error detected, keeping booking form open with error');
                setBookingError(response.error);
                setShowBookingError(false);
                return;
            }
            
            // For other errors, show the booking error view
            setShowBookingForm(false);
            setShowBookingConfirmation(false);
            setShowBookingError(true);
            setBookingError(response?.error || 'User already has a booking at this time.');
            
            return;
        }
        
        // CASE 2: Success response handling - only proceed if we have a definite success
        if (response && response.success === true) {
            try {
                // Refresh booked slots after successful booking
                if (clientId) {
                    const bookedSlotsData = await getCalendarBookings(clientId);
                    setBookings(bookedSlotsData || null);
                }
                
                // Clear any existing booking errors
                setBookingError(null);
                setShowBookingError(false);
                
                // Close the booking form for success responses
                setShowBookingForm(false);
                
                // Check if we have the necessary data for confirmation
                if (!response.start_time) {
                    // If missing data, show a generic success toast and message
                    toast.success('Booking confirmed! A confirmation email has been sent to you.');
                    
                    // Create a minimal booking info object for the confirmation screen
                    const genericBookingInfo: BookingInfo = {
                        slot: {
                            start: new Date().toISOString(), // Current time as fallback
                            end: new Date(new Date().getTime() + (selectedEvent?.duration || 30) * 60000).toISOString(),
                            eventTypeId: selectedEvent?.id || 'unknown',
                            scheduleName: selectedEvent?.title || 'Appointment'
                        },
                        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                    };
                    
                    // Show confirmation with generic data
                    setBookingInfo(genericBookingInfo);
                    setShowBookingConfirmation(true);
                    
                    // Reset selection states
                    setSelectedEvent(null);
                    setSelectedTime(null);
                    setSelectedDate(null);
                    return;
                }
                
                // Create booking info for the confirmation modal
                const bookingInfo: BookingInfo = {
                    slot: {
                        start: response.start_time,
                        end: response.end_time || new Date(new Date(response.start_time).getTime() + (selectedEvent?.duration || 30) * 60000).toISOString(),
                        eventTypeId: selectedEvent?.id,
                        scheduleName: selectedEvent?.title
                    },
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                };
                
                // Show success toast
                toast.success('Booking confirmed! You can see the details below.');
                
                // Show booking confirmation modal with close button and HIDE the calendar view
                setBookingInfo(bookingInfo);
                setShowBookingConfirmation(true);
                
                // Reset these specific states to prevent the booking form from reappearing
                setSelectedEvent(null);
                setSelectedTime(null);
                setSelectedDate(null);
            } catch (error) {
                console.error('Error refreshing booked slots:', error);
                // Continue with the rest of success handling even if refresh fails
            }
        } else {
            // Handle any unexpected response format
            toast.error('Unexpected response from booking service. Please try again.');
            resetBookingState();
        }
    };

    // Reset all booking-related state variables
    const resetBookingState = () => {
        // Reset selection states
        setSelectedEvent(null);
        setSelectedTime(null);
        setSelectedDate(null);
        
        // Reset UI visibility states
        setShowBookingForm(false);
        setShowBookingConfirmation(false);
        setShowBookingError(false);
        
        // Reset data states
        setBookingInfo(null);
        setBookingError(null);
        
        console.log('All booking state has been reset');
    };
    
    // Close booking form and reset state
    const handleCloseBookingForm = () => {
        setShowBookingForm(false);
        resetBookingState();
    };
    
    // Close booking confirmation and reset state
    const handleCloseConfirmation = () => {
        console.log('Closing booking confirmation and resetting all booking state');
        resetBookingState();
        
        // Small delay to ensure clean re-render of the calendar
        setTimeout(() => {
            // Force reload after confirmation is closed
            loadCalendarData(true);
        }, 300);
    };

    // Debug logging for state variables
    useEffect(() => {
        console.log('CalendarSettings state updated:', {
            showBookingForm,
            showBookingConfirmation,
            hasSelectedEvent: !!selectedEvent,
            hasSelectedTime: !!selectedTime,
            hasSelectedDate: !!selectedDate,
            hasBookingInfo: !!bookingInfo,
            hasBookingError: !!bookingError,
            loading,
            isConnected,
            calendarLoading
        });
    }, [showBookingForm, showBookingConfirmation, selectedEvent, selectedTime, selectedDate, bookingInfo, bookingError, loading, isConnected, calendarLoading]);

    // Render the loading state using the common Loader component
    if (loading && !calendarStatus) {
        return (
            <div className="h-full min-h-[600px] relative">
                <Loader 
                    fullPage={false} 
                    text="Loading calendar settings..." 
                    variant="pulse"
                    overlay={true} 
                />
            </div>
        );
    }

    if (error && !calendarStatus) {
        return <div className="p-6 text-red-600">Error: {error}</div>;
    }

    const renderSettingsForm = () => (
        <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-6">Connect Your Calendar</h2>
            
            <div className="space-y-4">
                <div>
                    <label htmlFor="calUsername" className="block text-sm font-medium text-gray-700 mb-1">
                        Cal.com Username
                    </label>
                    <input
                        id="calUsername"
                        type="text"
                        placeholder="your-username"
                        value={calUsername}
                        onChange={(e) => setCalUsername(e.target.value)}
                        className="w-full p-2 border rounded"
                    />
                    <div className="text-xs text-gray-500 mt-1">
                        Your username from Cal.com (e.g., "johndoe")
                    </div>
                </div>
                
                <div>
                    <label htmlFor="calApiKey" className="block text-sm font-medium text-gray-700 mb-1">
                        Cal.com API Key
                    </label>
                    <input
                        id="calApiKey"
                        type="password"
                        placeholder="cal_live_..."
                        value={calApiKey}
                        onChange={(e) => setCalApiKey(e.target.value)}
                        className="w-full p-2 border rounded"
                    />
                    <div className="text-xs text-gray-500 mt-1">
                        Get your API key from Cal.com dashboard (must start with "cal_live_")
                    </div>
                </div>

                <button
                    onClick={handleConnect}
                    disabled={isLoading || !calUsername || !calApiKey}
                    className={`
                        w-full p-2 rounded text-white relative
                        ${isLoading || !calUsername || !calApiKey 
                            ? 'bg-gray-300' 
                            : 'bg-blue-600 hover:bg-blue-700'}
                    `}
                >
                    {isLoading ? (
                        <div className="flex items-center justify-center">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            <span>Connecting...</span>
                        </div>
                    ) : (
                        'Connect Calendar'
                    )}
                </button>
            </div>
        </div>
    );

    // Add this helper function before the main component return statement
    const formatTimeIfValid = (timeString: string | null): string | undefined => {
        if (!timeString) return undefined;
        
        try {
            // Check if it's a valid date string
            const date = new Date(timeString);
            
            // Check if date is valid (not Invalid Date)
            if (isNaN(date.getTime())) {
                console.error('Invalid date detected:', timeString);
                return undefined;
            }
            
            return format(date, 'EEEE, MMMM d, yyyy h:mm a');
        } catch (error) {
            console.error('Error formatting date:', error, timeString);
            return undefined;
        }
    };

    // Render the main content based on connection status
    const renderContent = () => {
        // If loading, show the loading spinner
        if (loading) {
            return <div className="flex justify-center items-center h-40"><Loader size="medium" /></div>;
        }
        
        // If not connected, show setup form
        if (!isConnected) {
            return renderSettingsForm();
        }
        
        // If connected, show calendar
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-lg shadow p-5">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold">Booking Calendar</h2>
                        <button 
                            onClick={() => setShowSettings(!showSettings)}
                            className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                        >
                            <FontAwesomeIcon icon={faCog} className="mr-1" />
                            {showSettings ? 'Hide Settings' : 'Show Settings'}
                        </button>
                    </div>
                    
                    {showSettings && renderSettingsForm()}
                    
                    {!calendarStatus ? (
                        <div className="text-center p-4">
                            <Loader size="medium" />
                            <p className="mt-2">Loading calendar status...</p>
                        </div>
                    ) : (
                        <>
                            {/* CASE 1: Show Booking Confirmation */}
                            {showBookingConfirmation && bookingInfo && (
                                <div className="p-6">
                                    <div className="flex items-center mb-6">
                                        <button 
                                            onClick={handleCloseConfirmation}
                                            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                                        >
                                            <FontAwesomeIcon icon={faArrowLeft} />
                                            <span>Back to Calendar</span>
                                        </button>
                                    </div>
                                    
                                    <div className="text-center mb-8">
                                        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <FontAwesomeIcon icon={faCheck} className="text-white text-2xl" />
                                        </div>
                                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Booking Confirmed!</h2>
                                        <p className="text-gray-600">
                                            Your appointment has been scheduled successfully. A confirmation email has been sent.
                                        </p>
                                    </div>
                                    
                                    <BookingConfirmation
                                        bookingInfo={bookingInfo}
                                        onClose={handleCloseConfirmation}
                                    />
                                </div>
                            )}
                            
                            {/* CASE 2: Show Booking Error */}
                            {showBookingError && bookingError && selectedEvent && (
                                <div className="p-6">
                                    <div className="flex items-center mb-6">
                                        <button 
                                            onClick={handleCloseConfirmation}
                                            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                                        >
                                            <FontAwesomeIcon icon={faArrowLeft} />
                                            <span>Back to Calendar</span>
                                        </button>
                                    </div>
                                    
                                    <BookingError
                                        errorMessage={bookingError}
                                        eventTitle={selectedEvent?.title}
                                        selectedTime={formatTimeIfValid(selectedTime)}
                                        timeZone={calendarStatus?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                                        onBack={handleCloseConfirmation}
                                    />
                                </div>
                            )}
                            
                            {/* CASE 3: Show Booking Form */}
                            {showBookingForm && selectedEvent && selectedTime && !showBookingConfirmation && !showBookingError && (
                                <div className="p-6">
                                    <div className="flex items-center mb-6">
                                        <button 
                                            onClick={handleCloseBookingForm}
                                            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                                        >
                                            <FontAwesomeIcon icon={faArrowLeft} />
                                            <span>Back to Calendar</span>
                                        </button>
                                    </div>
                                    
                                    <h2 className="text-xl font-semibold mb-4">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-blue-600" />
                                        Book: {selectedEvent.title}
                                    </h2>
                                    
                                    {/* Error message if booking has an error */}
                                    {bookingError && (
                                        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
                                            <div className="flex items-start">
                                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mt-1 mr-3" />
                                                <div>
                                                    <h3 className="text-red-800 font-medium">Booking Failed</h3>
                                                    <p className="text-red-700 mt-1">{bookingError}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    <BookingForm
                                        eventId={selectedEvent.id}
                                        eventTitle={selectedEvent.title}
                                        selectedTime={selectedTime}
                                        displayTime={formatTimeIfValid(selectedTime)}
                                        duration={selectedEvent.duration || 30}
                                        timezone={selectedEvent.timezone || calendarStatus?.timezone || "America/New_York"}
                                        clientId={clientId || ''}
                                        onSuccess={handleBookingSuccess}
                                        onBack={handleCloseBookingForm}
                                        initialError={bookingError || undefined}
                                    />
                                </div>
                            )}
                            
                            {/* CASE 4: Show Calendar (default view) */}
                            {!showBookingConfirmation && !showBookingForm && !showBookingError && isConnected === true && (
                                <div>
                                    <div className="border-b border-gray-200 bg-gray-50 p-4">
                                        <h2 className="text-lg font-medium text-gray-800">
                                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-blue-600" />
                                            Select a Date & Time
                                        </h2>
                                        <p className="text-sm text-gray-600 mt-1">
                                            Click on a time slot to schedule your appointment
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {!calendarLoading && calendarStatus?.event_types?.length 
                                                ? `Showing ${calendarStatus.event_types.length} event types in ${calendarStatus.timezone || 'your timezone'}`
                                                : 'Loading calendar data...'}
                                        </p>
                                    </div>
                                    
                                    <div className="p-4 relative">
                                        {/* Show debug info */}
                                        <div className="mb-4 p-2 bg-gray-100 text-xs rounded">
                                            <div>Debug: Events available: {calendarStatus?.event_types?.length || 0}</div>
                                            <div>Debug: Calendar connected: {isConnected ? 'Yes' : 'No'}</div>
                                            <div>Debug: Timezone: {calendarStatus?.timezone || 'Not set'}</div>
                                            <div>Debug: Loading state: {calendarLoading ? 'Loading' : 'Ready'}</div>
                                            <div>Debug: Booked slots data: {bookings ? 'Available' : 'Not available'}</div>
                                        </div>
                                        
                                        {/* Show loader while calendar is loading */}
                                        {calendarLoading || !calendarStatus ? (
                                            <div className="min-h-[500px] relative">
                                                <Loader 
                                                    fullPage={false} 
                                                    text="Loading your calendar events..."
                                                    variant="pulse"
                                                    overlay={true}
                                                />
                                            </div>
                                        ) : calendarStatus?.event_types && calendarStatus.event_types.length > 0 ? (
                                            <>
                                                <div className="mb-3 p-3 border rounded bg-blue-50">
                                                    <h4 className="font-medium text-blue-800">Timezone Information</h4>
                                                    <p className="text-sm mt-1">
                                                        Times are shown in UTC (24-hour format). This matches the exact values from Cal.com.
                                                    </p>
                                                </div>
                                                <CalendarView
                                                    events={calendarStatus.event_types}
                                                    timezone={calendarStatus.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                                                    instanceId="calendar-settings-view"
                                                    onTimeSelect={handleTimeSelect}
                                                    initialBookings={[]}
                                                    preserveUTC={displayInUTC}
                                                    bookedSlots={bookings || undefined}
                                                    forceLightMode={true}
                                                    clientId={clientId || ''}
                                                />
                                            </>
                                        ) : (
                                            <div className="text-center p-8 text-gray-500">
                                                {isConnected 
                                                    ? 'No event types found. Please create event types in your Cal.com account.' 
                                                    : 'Connect your calendar to see available appointment slots.'}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="p-6">
            {renderContent()}
        </div>
    );
};

export default CalendarSettings;